'use client';
import { useState } from 'react';
import { useSite, useT } from '../../../lib/i18n';
import { pickName } from '../../../lib/i18n-core';
import type { DetailCurriculum, ProductDetail } from '../../../lib/site';
import { Icon } from '../../Icon';
import { SmartLink } from '../../SmartLink';
import { WishButton } from '../WishButton';
import { typeLabel } from '../labels';
import { limitParts, statusLabel } from '../price';

/**
 * 상세 상단 — 가이드 `pd-crumb` + `pd-top`(썸네일 / 정보).
 *
 * 정보 쪽은 넷이다 — 배지 줄 · 제목+맛보기 패널 · 스펙 3칸 · 메타 줄.
 *
 * **가이드에 있으나 그리지 않는 것**
 *  - `pd-head-row` 의 `상품 관리` 버튼 — 관리자용이라 프론트 상세에 있을 자리가 아니다.
 *  - 스펙 '상태' 칸의 `2026-05-17 개강` — 개강일이라는 칸이 상품에 없다(기수제 자리).
 *    판매 상태만 적고 부제는 신청기간이 있을 때만 쓴다.
 */
export function PdTop({ p, pointEnabled, adminHref }: {
  p: ProductDetail;
  /** 마켓이 포인트를 쓰나 — 상품 칸이 켜져 있어도 마켓이 끄면 포인트 얘기를 하지 않는다 */
  pointEnabled: boolean;
  /**
   * 관리자 상품 화면 주소 — **관리자로 로그인했을 때만** 넘어온다(2026-08-22 사용자 지시).
   * 가이드의 `상품 관리` 버튼이 이걸로 살아난다. 회원에게는 값 자체가 오지 않는다.
   */
  adminHref?: string;
}) {
  const t = useT();
  const { lang } = useSite();

  /*
   * 맛보기 — 자기 것이 있으면 그것만, 없으면 구성 단과에서(목록과 같은 규칙).
   * 설정 '맛보기 노출' 은 **목록 카드에만** 걸리는 값이라 여기서 보지 않는다
   * (현행도 `lecture_info.php` 는 그 값 없이 맛보기를 그린다 — 2026-08-23 사용자 지적).
   */
  const own = p.curriculumMaps.filter((m) => m.free);
  const previews: DetailCurriculum[] = own.length
    ? own
    : p.childMappings.flatMap((m) => m.child.curriculumMaps.filter((c) => c.free));

  const teacher = p.displayTeachers[0]?.teacher;
  const moreTeachers = p.displayTeachers.length - 1;
  const bookCount = p.childMappings.filter((m) => m.child.type === 'BOOK').length;
  /** 분류 — 얕은 것부터(브레드크럼 순서) */
  const crumbs = [...p.categories].sort((a, b) => a.category.depth - b.category.depth);

  /*
   * **스펙 3칸은 늘 있는 것으로 짠다**(2026-08-23 사용자 확정) —
   * 수강차시 · 수강기간 · 상태. 셋 다 상품이면 반드시 정해지는 값이다.
   *
   * 수강 제한은 **옵션**이라 칸을 차지하게 두지 않는다. 대신 관계 있는 칸의 **부제로 녹인다** —
   * 차시별 제한·1일 제한은 수강차시 밑에, 누적 수강 시간은 수강기간 밑에.
   * 안 걸어 둔 상품은 부제가 없을 뿐 칸 구성은 그대로다.
   */
  /*
   * 수강차시 — 패키지·프리패스는 **몇 강좌로 짜였는지**를 함께 적는다(2026-08-23 사용자 지시).
   * `2강좌 / 8강`. 단과는 자기 차시뿐이라 강좌 수를 말할 것이 없다.
   * `totalLessons` 는 P/F 도 구성 단과의 합으로 이미 채워져 있다(#1551 = 31강좌 277강).
   * 교재는 구성에서 뺀다 — 강좌 수를 세는 자리다.
   */
  const courseCount = p.childMappings.filter((m) => m.child.type !== 'BOOK').length;
  const lessons = [
    (p.type === 'PACKAGE' || p.type === 'FREEPASS') && courseCount > 0
      ? t('prd.courses', { n: courseCount }) : null,
    p.totalLessons ? t('prd.lessons', { n: p.totalLessons }) : null,
  ].filter(Boolean).join(' / ') || '-';
  const period = p.periodType === 'DATE'
    ? (p.periodEndDate ? t('prd.untilDate', { d: p.periodEndDate.slice(0, 10) }) : '-')
    : (p.periodDays ? t('prd.days', { n: p.periodDays }) : '-');
  const limits = limitParts(p, t);
  /**
   * 판매기간 — **없을 수 있다**(2026-08-23 사용자 지시). 신청기간을 안 쓰는 상품은
   * 늘 팔리므로 적을 기간이 없다. 그래서 셋째 칸의 주값은 **상태 배지**로 두고
   * 기간은 있을 때만 부제로 붙인다 — 값이 비는 칸이 생기지 않는다.
   */
  const ymd = (v: string | null) => (v ? v.slice(0, 10) : '');
  const salePeriod = p.applyPeriodEnabled && (p.applyStartDate || p.applyEndDate)
    ? `${ymd(p.applyStartDate)} ~ ${ymd(p.applyEndDate)}`.trim()
    : null;
  const status = statusLabel(p as never, t);
  /*
   * 결제 혜택 — 포인트는 마켓 설정이 켜져 있어야 뜻이 있다(주문 계산과 같은 조건).
   *
   * 이 바는 **상품 칸을 그대로** 말한다 — 혼자 사면 그게 사실이다. 혜택이 걸릴 때
   * 함께쓰기가 달라지는 것은 **혜택 카드가** 말한다(2026-08-24 사용자 제안) — 혜택은
   * 조건(개수)이 맞아야 걸리므로 여기서 미리 덮으면 혼자 사는 사람에게 거짓이 된다.
   */
  const useCoupon = p.couponEnabled;
  const usePoint = pointEnabled && p.pointEnabled;
  const earnRate = pointEnabled ? (p.pointRate ?? 0) : 0;

  return (
    <>
      <nav className="pd-crumb">
        <SmartLink href="/lectures">{t('nav.lectures')}</SmartLink>
        {crumbs.map((c, i) => (
          <span key={c.categoryId} className="d-flex ai-center" style={{ gap: 6 }}>
            <span className="sep">›</span>
            {i === crumbs.length - 1
              ? <span className="now">{pickName(c.category.name, lang)}</span>
              : <SmartLink href={`/lectures?categoryId=${c.categoryId}`}>{pickName(c.category.name, lang)}</SmartLink>}
          </span>
        ))}
      </nav>

      <div className="pd-top">
        <div className="pd-thumb">
          {p.image
            ? <img src={p.image} alt={p.name} />
            : <div className="pd-thumb-empty"><Icon name="image" /><span>{t('prd.noImage')}</span></div>}
          {/* 찜 — 목록 카드와 같은 하트를 쓴다(2026-08-23 사용자 지시). 자리만 상세에 맞춘다 */}
          <WishButton productId={p.id} wished={p.wished} />
        </div>

        <div className="pd-info">
          {/*
            가이드 상세의 배지 줄은 **종류 하나**뿐이고 모양도 목록과 다르다
            (`badge badge-outline-primary`). 목록 카드의 색 배지·추천 배지를 여기 옮겨 붙이지
            않는다 — 가이드가 상세에서 그 줄을 비워 둔 것이 결정이다(2026-08-22 사용자 지적).

            `상품 관리` 는 **관리자로 로그인했을 때만** 나온다(가이드에는 늘 있지만 그건
            견본이라 그렇다). 새 창으로 연다 — 보던 상품 페이지를 잃지 않아야 한다.
          */}
          <div className="pd-head-row">
            <span className="badge badge-outline-primary">{typeLabel(p.type, t)}</span>
            {adminHref && (
              <a className="badge badge-dark" href={adminHref} target="_blank" rel="noopener noreferrer">
                <Icon name="settings" /> {t('prd.manageProduct')}
              </a>
            )}
          </div>

          <div className="pd-title-row">
            <h1 className="pd-title">{p.name}</h1>
            {previews.length > 0 && <PreviewPanel items={previews} />}
          </div>

          <div className="pd-spec-grid">
            <div className="pd-spec filter">
              <div className="pd-spec-lbl">{t('prd.spec.lessons')}</div>
              <div className="pd-spec-val">{lessons}</div>
              {/*
                차시에 걸린 제한 — 차시별 배수·시간, 1일 제한.
                **둘 다 걸려 있으면 줄을 나눈다**(2026-08-23 사용자 지시) — 가운뎃점으로 이으면
                `차시당 1시간 20분 제한 · 하루 10차시 제한` 이 한 줄에 들어가 읽히지 않는다.
                **안 걸어 뒀으면 `무제한`** 이라고 적는다 — 비워 두면 '아직 안 정해진 값' 같다.
              */}
              {limits.lesson || limits.daily.length > 0
                ? [limits.lesson, ...limits.daily].filter(Boolean)
                  .map((x) => <div key={x!.long} className="pd-spec-sub">{x!.long}</div>)
                : <div className="pd-spec-sub">{t('prd.limit.none')}</div>}
            </div>
            <div className="pd-spec filter">
              <div className="pd-spec-lbl">{t('prd.spec.studyPeriod')}</div>
              <div className="pd-spec-val">{period}</div>
              {/* 기간에 걸린 제한 — 누적 수강 가능 시간. 없으면 `무제한` */}
              <div className="pd-spec-sub">{limits.lecture?.long ?? t('prd.limit.none')}</div>
            </div>
            <div className="pd-spec filter">
              <div className="pd-spec-lbl">{t('prd.spec.status')}</div>
              {/* 상태 문구는 목록·카드와 **같은 판정**을 쓴다(price.statusLabel) — 두 벌로 두면 갈라진다 */}
              <div className="pd-spec-val">
                <span className={`badge badge-pill filter ${status ? 'badge-warning' : 'badge-info'}`}>
                  {status?.text ?? t('prd.status.onSale')}
                </span>
              </div>
              {salePeriod && <div className="pd-spec-sub">{salePeriod}</div>}
            </div>
          </div>

          <div className="pd-meta-row">
            {teacher && (
              <div className="pd-meta-item">
                <Icon name="user" />
                {moreTeachers > 0 ? t('prd.teacherMore', { name: teacher.name ?? '', n: moreTeachers }) : teacher.name}
              </div>
            )}
            {bookCount > 0 && (
              <>
                {teacher && <div className="pd-meta-sep" />}
                <div className="pd-meta-item"><Icon name="pen-line" />{t('prd.hasBook')}</div>
              </>
            )}
            {/*
              결제 혜택(2026-08-23 사용자 지시) — `쿠폰 / 포인트 사용 가능`, 적립률이 있으면
              `{n}% 포인트 적립` 을 따로 한 칸 더. 포인트 쪽은 **마켓 설정까지** 봐야 한다 —
              상품 칸이 켜져 있어도 마켓이 포인트를 끄면 주문에서 무효다(order.service 와 같은 판정).
            */}
            {(useCoupon || usePoint) && (
              <>
                {(teacher || bookCount > 0) && <div className="pd-meta-sep" />}
                <div className="pd-meta-item">
                  <Icon name="ticket-percent" />
                  {`${[useCoupon ? t('prd.coupon') : null, usePoint ? t('prd.point') : null]
                    .filter(Boolean).join(' / ')} ${t('prd.useOk')}`}
                </div>
              </>
            )}
            {earnRate > 0 && (
              <>
                <div className="pd-meta-sep" />
                <div className="pd-meta-item"><Icon name="coins" />{t('prd.pointEarn', { n: earnRate })}</div>
              </>
            )}
            {/* 일시정지·연장은 **횟수가 있을 때만** 말한다(0이면 못 쓴다는 뜻이다) */}
            {((p.pauseCount ?? 0) > 0 || (p.extendCount ?? 0) > 0) && (
              <>
                {(teacher || bookCount > 0 || useCoupon || usePoint || earnRate > 0)
                  && <div className="pd-meta-sep" />}
                <div className="pd-meta-item">
                  <Icon name="clock-3" />
                  {/* 가이드 문구 그대로 — 끝에 '가능' 이 붙는다 */}
                  {`${[(p.pauseCount ?? 0) > 0 ? t('prd.pauseOk') : null,
                    (p.extendCount ?? 0) > 0 ? t('prd.extendOk') : null].filter(Boolean).join(' / ')} ${t('prd.available')}`}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * 맛보기 패널 — 가이드 `course-preview-wrap`. 버튼을 누르면 목록이 열린다.
 * 목록 카드의 드롭다운과 달리 **회차 번호와 재생 표시**가 붙는 큰 패널이다.
 */
function PreviewPanel({ items }: { items: DetailCurriculum[] }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="course-preview-wrap">
      <div className="course-links">
        <button type="button" className="badge-pill action-btn action-submit filter"
          onClick={() => setOpen(!open)}>
          <Icon name="play" /> {t('prd.preview')}
        </button>
      </div>
      {open && (
        <div className="course-preview-drop" style={{ display: 'block' }}>
          <div className="preview-panel-header">
            <Icon name="circle-play" />
            <span>{t('prd.previewPanel')}</span>
            <span className="preview-panel-cnt">{t('prd.lessons', { n: items.length })}</span>
          </div>
          <div className="preview-items">
            {items.map((m, i) => (
              // 재생은 ⑤ 플레이어 단계다 — 지금은 자리만 두고 누르면 아무 일도 하지 않는다
              <span key={m.curriculumId} className="preview-item">
                <span className="preview-item-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="preview-item-name">{m.curriculum.name}</span>
                <span className="preview-item-play"><Icon name="play" /> {t('prd.play')}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
